import { formatLabel } from '../utils/formatText';
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ShoppingCart, Trash2, Send, Plus, Minus, ArrowLeft, Search, Package, X, MapPin, Truck, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useQuoteCart } from '../context/QuoteCartContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Orcamento = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { settings } = useSiteSettings();
  const { items, addProduct, addVariation, removeItem, updateQuantity, isInCart, totalItems, clearCart } = useQuoteCart();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCatalog, setShowCatalog] = useState(true);
  const [deliveryOption, setDeliveryOption] = useState('retirada');
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cpfCnpj: '',
    street: '',
    neighborhood: '',
    city: '',
    message: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, brandsRes, categoriesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/products`),
          axios.get(`${BACKEND_URL}/api/brands`),
          axios.get(`${BACKEND_URL}/api/categories`)
        ]);
        
        if (productsRes.data?.length > 0) setProducts(productsRes.data);
        if (brandsRes.data?.length > 0) setBrands(brandsRes.data);
        if (categoriesRes.data?.length > 0) setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os produtos.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [toast]);

  // Carregar produto da URL
  useEffect(() => {
    const productId = searchParams.get('produto');
    const variationId = searchParams.get('variacao');
    
    if (productId && products.length > 0) {
      const product = products.find(p => p.id === productId);
      if (product) {
        // Se tem variação específica na URL
        if (variationId && product.variations?.length > 0) {
          const variation = product.variations.find(v => v.id === variationId);
          if (variation && !isInCart(product.id, variation.id)) {
            addVariation(product, variation);
            toast({ title: `${product.name} - ${variation.name} adicionado ao orçamento!` });
          }
        } else if (product.variations?.length > 0) {
          // Produto tem variações - expandir para seleção
          setExpandedProduct(product.id);
        } else {
          // Produto simples sem variações
          if (!isInCart(product.id)) {
            addProduct(product);
            toast({ title: `${product.name} adicionado ao orçamento!` });
          }
        }
      }
    }
  }, [searchParams, products]);

  // Adicionar produto simples do catálogo embutido
  const handleAddProduct = (product) => {
    if (isInCart(product.id)) {
      toast({ title: "Este produto já está no orçamento", variant: "destructive" });
      return;
    }
    addProduct(product);
    toast({ title: `${product.name} adicionado ao orçamento!` });
  };

  // Adicionar variação do catálogo embutido
  const handleAddVariation = (product, variation) => {
    if (isInCart(product.id, variation.id)) {
      toast({ title: "Esta variação já está no orçamento", variant: "destructive" });
      return;
    }
    addVariation(product, variation);
    toast({ title: `${product.name} - ${variation.name} adicionado ao orçamento!` });
    setExpandedProduct(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
    } else {
      return numbers.slice(0, 11).replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (items.length === 0) {
      toast({
        title: "Atenção",
        description: "Adicione pelo menos um produto ao orçamento.",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/track/quote?customer_name=${encodeURIComponent(formData.name)}&customer_phone=${encodeURIComponent(formData.phone)}`, null, {
        params: { products: items.map(item => item.name) }
      });
    } catch (e) {
      console.log('Track error:', e);
    }

    const itemList = items
      .map(item => `- ${item.name} (${item.brand})${item.size ? ` - ${item.size}` : ''} - Qtd: ${item.quantity}`)
      .join('\n');

    const deliveryText = deliveryOption === 'retirada' ? 'Retirar na loja' : 'Entrega';
    
    // Montar endereço completo
    const addressParts = [];
    if (formData.street) addressParts.push(formData.street);
    if (formData.neighborhood) addressParts.push(formData.neighborhood);
    if (formData.city) addressParts.push(formData.city);
    const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : 'Não informado';
    
    const whatsappMessage = encodeURIComponent(
      `*SOLICITAÇÃO DE ORÇAMENTO*\n\n` +
      `*Cliente:* ${formData.name}\n` +
      `*CPF/CNPJ:* ${formData.cpfCnpj || 'Não informado'}\n` +
      `*E-mail:* ${formData.email}\n` +
      `*Telefone:* ${formData.phone}\n` +
      `*Endereço:* ${fullAddress}\n` +
      `*Forma de recebimento:* ${deliveryText}\n\n` +
      `*PRODUTOS:*\n${itemList}\n\n` +
      `*Total de itens:* ${items.reduce((acc, item) => acc + item.quantity, 0)}\n\n` +
      `*Observações:* ${formData.message || 'Nenhuma'}\n\n` +
      `_Enviado pelo site Disneylândia Cosméticos_`
    );

    toast({
      title: "Orçamento enviado!",
      description: "Você será redirecionado para o WhatsApp.",
    });

    setTimeout(() => {
      window.open(`https://wa.me/${settings.site_whatsapp}?text=${whatsappMessage}`, '_blank');
    }, 1000);
  };

  // Filtrar produtos
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.brand.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBrand = !selectedBrand || product.brand === selectedBrand;
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    return matchesSearch && matchesBrand && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-8" data-testid="orcamento-page">
      <div className="container mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="mb-8">
          <Link to="/catalogo" className="inline-flex items-center text-[#EA1266] hover:text-[#d00f58] text-sm font-medium mb-4">
            <ArrowLeft size={16} className="mr-2" /> Voltar ao Catálogo
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#EA1266]/10 rounded-2xl flex items-center justify-center">
              <ShoppingCart size={28} className="text-[#EA1266]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-medium text-[#0A0A0A] tracking-tight" data-testid="orcamento-title">Solicitar Orçamento</h1>
              <p className="text-[#4B5563] font-medium">Selecione os produtos e envie pelo WhatsApp</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Coluna Esquerda - Catálogo */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-[#0A0A0A] flex items-center gap-2">
                    <Package size={20} className="text-[#EA1266]" />
                    Produtos Disponíveis
                  </h2>
                  <Button variant="ghost" size="sm" onClick={() => setShowCatalog(!showCatalog)} className="text-[#EA1266]">
                    {showCatalog ? 'Ocultar' : 'Mostrar'}
                  </Button>
                </div>

                {showCatalog && (
                  <>
                    {/* Filtros */}
                    <div className="flex flex-wrap gap-3 mb-4">
                      <div className="relative flex-1 min-w-[200px]">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                          placeholder="Buscar produto..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-9 rounded-xl border-gray-200"
                          data-testid="search-input"
                        />
                      </div>
                      <select
                        value={selectedBrand}
                        onChange={(e) => setSelectedBrand(e.target.value)}
                        className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white"
                      >
                        <option value="">Todas as marcas</option>
                        {brands.map(brand => (
                          <option key={brand.id} value={brand.name}>{formatLabel(brand.name)}</option>
                        ))}
                      </select>
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="px-3 py-2 rounded-xl border border-gray-200 text-sm bg-white"
                      >
                        <option value="">Todas categorias</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{formatLabel(cat.name)}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {items.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-[#EA1266]/5 rounded-xl mb-4">
                    <span className="text-sm font-medium text-[#EA1266]">
                      {items.length} {items.length === 1 ? 'item selecionado' : 'itens selecionados'} ({totalItems} unidades)
                    </span>
                    <Button variant="ghost" size="sm" onClick={clearCart} className="text-red-500 hover:text-red-600">
                      <X size={14} className="mr-1" /> Limpar
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {showCatalog && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {loading ? (
                  <div className="col-span-2 text-center py-16">
                    <div className="w-8 h-8 border-3 border-[#EA1266] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-400 font-medium">Carregando produtos...</p>
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className="col-span-2 text-center py-16">
                    <Package size={40} className="mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-400 font-medium">Nenhum produto encontrado</p>
                  </div>
                ) : (
                  filteredProducts.map((product) => {
                    const hasVariations = product.variations && product.variations.length > 0;
                    const isExpanded = expandedProduct === product.id;
                    const productSelected = !hasVariations && isInCart(product.id);
                    
                    return (
                      <Card 
                        key={product.id} 
                        className={`overflow-hidden transition-all rounded-2xl border ${
                          productSelected ? 'border-[#64BC46] bg-green-50/30' : 
                          isExpanded ? 'border-[#EA1266] bg-pink-50/30' : 
                          'border-gray-100 hover:shadow-lg'
                        }`}
                        data-testid={`product-item-${product.id}`}
                      >
                        <div className="flex">
                          <div className="w-28 h-28 bg-[#F3F4F6] flex-shrink-0">
                            <img
                              src={product.images?.[0] || 'https://placehold.co/150x150/F3F4F6/999?text=Produto'}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <CardContent className="p-3 flex-1 flex flex-col">
                            <Badge className="self-start mb-1 text-xs bg-[#0A0A0A] text-white rounded-full">{product.brand}</Badge>
                            <h3 className="font-medium text-[#0A0A0A] text-sm line-clamp-2 mb-1">{product.name}</h3>
                            
                            {/* Info de variações e tamanho */}
                            <div className="flex flex-wrap gap-1 mb-2">
                              {product.size && (
                                <span className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">{product.size}</span>
                              )}
                              {hasVariations && (
                                <span className="text-[10px] bg-pink-50 px-1.5 py-0.5 rounded text-[#EA1266] flex items-center gap-0.5">
                                  <Layers size={10} />
                                  {product.variations.length} {product.variations.length === 1 ? 'opção' : 'opções'}
                                </span>
                              )}
                            </div>
                            
                            <div className="mt-auto">
                              {hasVariations ? (
                                // Produto com variações - mostrar botão para expandir
                                <Button
                                  size="sm"
                                  className={`w-full rounded-full text-xs ${
                                    isExpanded 
                                      ? 'bg-[#EA1266] text-white hover:bg-[#d00f58]' 
                                      : 'bg-pink-50 text-[#EA1266] hover:bg-pink-100'
                                  }`}
                                  onClick={() => setExpandedProduct(isExpanded ? null : product.id)}
                                >
                                  <Layers size={14} className="mr-1" />
                                  {isExpanded ? 'Fechar opções' : 'Ver opções'}
                                </Button>
                              ) : productSelected ? (
                                // Produto simples selecionado
                                <div className="flex items-center justify-between">
                                  <span className="text-[#64BC46] text-xs font-medium">No orçamento</span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50 p-1 h-auto rounded-full"
                                    onClick={() => removeItem(product.id)}
                                  >
                                    <X size={16} />
                                  </Button>
                                </div>
                              ) : (
                                // Produto simples não selecionado
                                <Button
                                  size="sm"
                                  className="w-full bg-[#EA1266] hover:bg-[#d00f58] text-white rounded-full text-xs"
                                  onClick={() => handleAddProduct(product)}
                                  data-testid={`add-product-${product.id}`}
                                >
                                  <Plus size={14} className="mr-1" /> Adicionar
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </div>
                        
                        {/* Painel de variações expandido */}
                        {hasVariations && isExpanded && (
                          <div className="border-t border-gray-100 p-3 bg-gray-50/50">
                            <p className="text-xs font-medium text-gray-600 mb-2">Selecione uma opção:</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {product.variations.map((variation) => {
                                const variationSelected = isInCart(product.id, variation.id);
                                return (
                                  <div
                                    key={variation.id}
                                    className={`p-2 rounded-lg border-2 transition-all cursor-pointer ${
                                      variationSelected 
                                        ? 'border-[#64BC46] bg-green-50' 
                                        : 'border-gray-100 bg-white hover:border-[#EA1266]'
                                    }`}
                                    onClick={() => {
                                      if (variationSelected) {
                                        removeItem(`${product.id}-var-${variation.id}`);
                                      } else {
                                        handleAddVariation(product, variation);
                                      }
                                    }}
                                  >
                                    {variation.image && (
                                      <div className="w-full aspect-square rounded overflow-hidden mb-1.5 bg-white">
                                        <img src={variation.image} alt={variation.name} className="w-full h-full object-cover" />
                                      </div>
                                    )}
                                    <p className="text-xs font-medium text-center truncate">{variation.name}</p>
                                    {variation.size && (
                                      <p className="text-[10px] text-gray-500 text-center">{variation.size}</p>
                                    )}
                                    {variationSelected && (
                                      <p className="text-[10px] text-[#64BC46] text-center font-medium mt-1">✓ Selecionado</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Coluna Direita - Formulário e Itens */}
          <div className="space-y-6">
            {/* Itens Selecionados */}
            <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-lg font-semibold text-[#0A0A0A] mb-4 flex items-center gap-2">
                  <ShoppingCart size={20} className="text-[#EA1266]" />
                  Itens no Orçamento
                  {items.length > 0 && (
                    <Badge className="bg-[#EA1266] text-white ml-2">{totalItems}</Badge>
                  )}
                </h3>

                {items.length === 0 ? (
                  <div className="text-center py-8">
                    <Package size={32} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-400 text-sm">Nenhum item adicionado</p>
                    <p className="text-gray-300 text-xs mt-1">Selecione produtos ao lado</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {items.map((item) => (
                      <div 
                        key={item.itemKey} 
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl"
                        data-testid={`selected-item-${item.itemKey}`}
                      >
                        <div className="w-14 h-14 bg-white rounded-lg overflow-hidden flex-shrink-0 border">
                          {item.image ? (
                            <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package size={20} className="text-gray-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-[#0A0A0A] truncate">{item.name}</p>
                          <p className="text-xs text-gray-500">{item.brand}</p>
                          {item.size && <p className="text-xs text-gray-400">{item.size}</p>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-7 h-7 p-0 rounded-full"
                            onClick={() => updateQuantity(item.itemKey, item.quantity - 1)}
                          >
                            <Minus size={12} />
                          </Button>
                          <span className="w-8 text-center font-semibold text-sm">{item.quantity}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-7 h-7 p-0 rounded-full"
                            onClick={() => updateQuantity(item.itemKey, item.quantity + 1)}
                          >
                            <Plus size={12} />
                          </Button>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50 p-1 h-auto rounded-full"
                          onClick={() => removeItem(item.itemKey)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Formulário de Dados */}
            <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
              <CardContent className="p-5">
                <h3 className="text-lg font-semibold text-[#0A0A0A] mb-4">Seus Dados</h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">Nome Completo *</label>
                    <Input name="name" value={formData.name} onChange={handleChange} required placeholder="Seu nome" className="rounded-xl border-gray-200" data-testid="input-name" />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">E-mail *</label>
                    <Input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="seu@email.com" className="rounded-xl border-gray-200" data-testid="input-email" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">Telefone *</label>
                      <Input type="tel" name="phone" value={formData.phone} onChange={handlePhoneChange} required placeholder="(00) 00000-0000" maxLength={15} className="rounded-xl border-gray-200" data-testid="input-phone" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">CPF/CNPJ</label>
                      <Input name="cpfCnpj" value={formData.cpfCnpj} onChange={handleChange} placeholder="000.000.000-00" className="rounded-xl border-gray-200" data-testid="input-cpf" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">Rua / Endereço</label>
                    <Input name="street" value={formData.street} onChange={handleChange} placeholder="Ex: Av. Weyne Cavalcante, 720" className="rounded-xl border-gray-200" data-testid="input-street" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">Bairro</label>
                      <Input name="neighborhood" value={formData.neighborhood} onChange={handleChange} placeholder="Ex: Centro" className="rounded-xl border-gray-200" data-testid="input-neighborhood" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">Cidade</label>
                      <Input name="city" value={formData.city} onChange={handleChange} placeholder="Ex: Canaã dos Carajás" className="rounded-xl border-gray-200" data-testid="input-city" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#4B5563] mb-3 uppercase tracking-wider">Como deseja receber? *</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setDeliveryOption('retirada')}
                        className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          deliveryOption === 'retirada' 
                            ? 'border-[#EA1266] bg-pink-50 text-[#EA1266]' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="delivery-pickup"
                      >
                        <MapPin size={20} />
                        <span className="text-xs font-medium">Retirar na loja</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeliveryOption('entrega')}
                        className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          deliveryOption === 'entrega' 
                            ? 'border-[#EA1266] bg-pink-50 text-[#EA1266]' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid="delivery-home"
                      >
                        <Truck size={20} />
                        <span className="text-xs font-medium">Entrega</span>
                      </button>
                    </div>
                    {deliveryOption === 'entrega' && (
                      <p className="text-[10px] text-[#4B5563] mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5 font-medium" data-testid="delivery-fee-notice">
                        A taxa de entrega será calculada e informada durante o atendimento no WhatsApp.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#4B5563] mb-1.5 uppercase tracking-wider">Observações</label>
                    <Textarea name="message" value={formData.message} onChange={handleChange} rows={3} placeholder="Informações adicionais..." className="rounded-xl border-gray-200" data-testid="input-message" />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#64BC46] hover:bg-[#56a33c] text-white h-14 rounded-full text-sm uppercase tracking-wider font-bold shadow-lg shadow-green-500/20 hover:shadow-green-500/40 transition-all"
                    disabled={items.length === 0}
                    data-testid="submit-quote-btn"
                  >
                    <Send size={18} className="mr-2 flex-shrink-0" />
                    <span className="truncate">Enviar Orçamento via WhatsApp</span>
                  </Button>
                  
                  <p className="text-[10px] text-gray-400 text-center">
                    Ao clicar, você será direcionado para o WhatsApp
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
